import { webApiService } from '@/services'
import { BATTERY_CRITERIA, BATTERY_HEALTH_VS_CHARGE, getBatteryInstructions } from '@/utils/batteryInstructions'
import { hasGradeQuality, isContractGenerated } from '@/utils/batteryStep'
import { brandLogo } from '@/utils/brandLogo'
import { Chip, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Switch, cn } from '@nextui-org/react'
import { enqueueSnackbar } from 'notistack'
import React, { useEffect, useMemo, useState } from 'react'
import { MdBatteryChargingFull } from 'react-icons/md'
import { useParams } from 'react-router-dom'
import { Button, useButtonClasses } from '@/components/ui/button'
import StepCard from './StepCard'
import { useStepFooterAction } from '../context/StepFooterActionContext'
import { useTradeinContext } from '../hooks/useTradeinContext'

interface ISwitchBatteryProps {
  /* true = bateria superior al 80%, false = bateria menor al 80% */
  onValueChange?: (isBatteryHealthy: boolean) => void
  /* true = el equipo no entrega dato de salud de batería, no se evalúa el 80% */
  onNoHealthDataChange?: (hasNoHealthData: boolean) => void
  isDisabled?: boolean
  label?: string
  className?: string
}

const SwitchBattery: React.FC<ISwitchBatteryProps> = ({
  onValueChange,
  // onNoHealthDataChange,
  isDisabled = false,
  label = '¿La capacidad de carga del dispositivo es superior al 80%?',
  className
}) => {
  const { ctx, saveCtx } = useTradeinContext()
  const buttonClasses = useButtonClasses()
  const { id: idParam } = useParams()
  // Fuera de la ruta del detalle (modal del listado) el id no viene en la url, se toma del contexto.
  const id = idParam ?? String(ctx.tradein.id)
  /* isBatteryHealthy en BD: 1 = superior al 80%, 0 = menor al 80%, null = sin evaluar */
  const [isBatteryHealthy, setIsBatteryHealthy] = useState(ctx.tradein.isBatteryHealthy !== 0)
  /* marcado cuando el equipo no entrega % de salud: oculta el switch */
  // const [hasNoHealthData, setHasNoHealthData] = useState(false)
  const [openInstructions, setOpenInstructions] = useState(false)
  const [loading, setLoading] = useState(false)
  /* pasos que fallaron al cargar su imagen, para no mostrar el espacio vacío */
  const [imageErrors, setImageErrors] = useState<number[]>([])

  const instructions = useMemo(() => getBatteryInstructions(ctx.tradein.brand), [ctx.tradein.brand])
  const logo = brandLogo(ctx.tradein.brand ?? '')
  /* el estado de batería solo se puede registrar en Cotizado (1), Aprobado (2) y Validado (4) */
  const isEditableStatus = [1, 2, 4].includes(ctx.tradein.id_status)
  /* el guardado escribe el grade quality y con eso el paso queda cerrado: se registra una sola vez.
     isBatteryHealthy no sirve como marcador porque al llegar acá ya viene en 1 de un paso anterior. */
  const isLocked = isDisabled || !isEditableStatus || isContractGenerated(ctx.tradein) || hasGradeQuality(ctx.tradein)

  useEffect(() => {
    if (ctx.tradein.isBatteryHealthy === null) return
    setIsBatteryHealthy(ctx.tradein.isBatteryHealthy === 1)
  }, [ctx.tradein.isBatteryHealthy])

  useEffect(() => {
    setImageErrors([])
  }, [instructions])

  const handleValueChange = (value: boolean) => {
    setIsBatteryHealthy(value)
    if (onValueChange) onValueChange(value)
  }

  // const handleNoHealthDataChange = (value: boolean) => {
  //   setHasNoHealthData(value)
  //   if (onNoHealthDataChange) onNoHealthDataChange(value)
  // }

  const handleSave = async () => {
    setLoading(true)
    try {
      /* check marcado = no había cómo revisar el estado: haveBatteryStatus 0 y el switch viaja en null */
      await webApiService.updateBatteryHealth({
        idTradein: Number(id),
        // haveBatteryStatus: hasNoHealthData ? 0 : 1,
        isBatteryHealthy: (isBatteryHealthy ? 1 : 0)
      })
      enqueueSnackbar('Estado de batería actualizado con éxito', { variant: 'success' })
      saveCtx({ ...ctx, reload: true })
    } catch (error: any) {
      const msg = error.response?.data.message ?? error.message ?? 'Error al actualizar el estado de la batería'
      enqueueSnackbar(msg, { variant: 'error' })
      console.log('error ==>', msg)
    } finally {
      setLoading(false)
    }
  }

  const inlineAction = useStepFooterAction(
    (
      <Button
        size='sm'
        disabled={loading || isLocked}
        onClick={handleSave}
      >
        {loading ? 'Guardando...' : 'Guardar'}
      </Button>
    ),
    [loading, isLocked]
  )

  return (
    <StepCard
      icon={<MdBatteryChargingFull />}
      title='Estado de la batería'
      className='h-full'
      right={(
        <Button
          size='sm'
          onClick={() => { setOpenInstructions(true) }}
        >
          Ver instrucciones
        </Button>
      )}
    >
      <p className='mb-2 rounded-lg bg-warning-50 p-2 text-tiny text-warning-700'>
        <strong>Importante:</strong> {BATTERY_HEALTH_VS_CHARGE}
      </p>

      {/* <Checkbox
        size='sm'
        isSelected={hasNoHealthData}
        onValueChange={handleNoHealthDataChange}
        isDisabled={isDisabled || ctx.tradein.isBatteryHealthy !== null}
        classNames={{ base: 'mb-2 max-w-full', label: 'text-small' }}
      >
        Sin dato de salud, el equipo no cuenta con alguna forma para revisar el % de salud de la batería
        {instructions.registry === 'none'
          ? <span className='block text-tiny text-default-500'>{instructions.brand} no entrega % de salud en ningún modelo.</span>
          : null}
      </Checkbox> */}

      {/* por ahora se asume que el equipo siempre entrega dato de salud, así que el switch se muestra siempre.
          Para volver a soportar el caso «sin dato de salud»: descomentar `{!hasNoHealthData ? (` de abajo,
          el `) : (<div .../>)}` del final y el Checkbox de arriba. */}
      {/* {!hasNoHealthData
        ? ( */}
      <Switch
        color='success'
        isSelected={isBatteryHealthy}
        onValueChange={handleValueChange}
        isDisabled={isLocked}
        classNames={{
          base: cn(
            'inline-flex flex-row-reverse w-full max-w-full bg-content1 hover:bg-content2 items-center',
            'justify-between cursor-pointer rounded-lg gap-2 p-4 border-2 border-transparent',
            'data-[selected=true]:border-success',
            className
          ),
          wrapper: 'p-0 h-4 overflow-visible',
          thumb: cn(
            'w-6 h-6 border-2 shadow-lg',
            'group-data-[hover=true]:border-success',
            'group-data-[selected=true]:ms-6',
            'group-data-[pressed=true]:w-7',
            'group-data-[selected]:group-data-[pressed]:ms-4'
          )
        }}
      >
        <div className='flex flex-col gap-1'>
          <p className='text-medium'>{label}</p>
          {/* <p className='text-medium text-default-700'>
            Estado: <strong style={{ color: isBatteryHealthy ? 'green' : '#3f3f46' }}>{isBatteryHealthy ? 'Superior al 80%' : 'Menor al 80%'}</strong>
          </p> */}
        </div>
      </Switch>
      {/* )
        : (
          <div className='rounded-lg bg-default-100 p-4'>
            <p className='text-small font-semibold'>Sin dato de salud de batería</p>
            <p className='text-tiny text-default-600'>El equipo no entrega el % de salud, por lo que no se evalúa el criterio del 80%.</p>
          </div>
          )} */}

      <Modal backdrop='blur' size='4xl' scrollBehavior='inside' isOpen={openInstructions} onClose={() => { setOpenInstructions(false) }}>
        <ModalContent>
          <ModalHeader className='flex items-center gap-3'>
            {logo !== '' ? <img src={logo} alt={instructions.brand} className='h-6 w-auto object-contain' /> : null}
            <div className='flex flex-col'>
              <span>{instructions.title}</span>
              <span className='text-tiny font-normal text-default-500'>Cómo revisar la salud de la batería</span>
            </div>
            <Chip size='sm' variant='flat' color={instructions.registry === 'none' ? 'default' : instructions.registry === 'native' ? 'success' : 'warning'}>
              {instructions.registryLabel}
            </Chip>
          </ModalHeader>
          <ModalBody>
            <div className='rounded-lg border-1 border-warning-200 bg-warning-50 p-3'>
              <p className='text-small text-warning-700'>
                <strong>Importante:</strong> {BATTERY_HEALTH_VS_CHARGE} Lo que se registra es la salud (capacidad máxima) de la batería, no el nivel de carga.
              </p>
            </div>

            <p className='text-small text-default-700'>{instructions.summary}</p>

            {/* pasos en columnas (una al lado de la otra) para evitar el scroll vertical */}
            {instructions.steps.length > 0
              ? (
                <ol className='flex flex-wrap justify-center gap-4'>
                  {instructions.steps.map((step, index) => (
                    /* w-[calc(50%-0.5rem)] = 2 por fila con el gap-4; el último impar queda centrado */
                    <li key={index} className='flex w-full flex-col gap-2 sm:w-[calc(50%-0.5rem)]'>
                      <div className='flex items-center gap-2'>
                        <div className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-tiny font-bold text-white'>
                          {index + 1}
                        </div>
                        <p className='text-small font-semibold'>{step.title}</p>
                      </div>
                      {step.description !== undefined ? <p className='text-tiny text-default-700'>{step.description}</p> : null}
                      {step.image !== undefined && !imageErrors.includes(index)
                        ? (
                          <img
                            src={step.image}
                            alt={step.title}
                            className='mt-auto max-h-80 w-full rounded-lg border-1 border-default-200 object-contain'
                            onError={() => { setImageErrors((prev) => [...prev, index]) }}
                          />
                          )
                        : null}
                      {step.note !== undefined ? <p className='text-tiny text-default-500 italic'>{step.note}</p> : null}
                    </li>
                  ))}
                </ol>
                )
              : (
                <div className='rounded-lg bg-default-100 p-3'>
                  <p className='text-small font-semibold'>No hay dato de salud que recoger</p>
                  <p className='text-tiny text-default-600'>Ningún modelo de esta marca entrega el % de salud. Márcalo como «sin dato de salud».</p>
                </div>
                )}

            {instructions.stateMapping !== undefined
              ? (
                <div className='rounded-lg border-1 border-default-200 p-3'>
                  <p className='mb-2 text-tiny font-bold uppercase text-default-500'>Homologar el estado</p>
                  <div className='flex flex-col gap-1'>
                    {instructions.stateMapping.map((item) => (
                      <div key={item.state} className='flex items-center gap-2 text-small'>
                        <span className='font-semibold'>{item.state}</span>
                        <span className='text-default-400'>→</span>
                        <span className={item.result === 'Sobre 80%' ? 'text-success font-semibold' : 'text-danger font-semibold'}>{item.result}</span>
                      </div>
                    ))}
                  </div>
                </div>
                )
              : null}

            {instructions.supported !== undefined
              ? (
                <div className='rounded-lg border-1 border-success-200 bg-success-50 p-3'>
                  <p className='mb-2 text-tiny font-bold uppercase text-success-700'>{instructions.supported.label}</p>
                  {instructions.supported.models !== undefined
                    ? (
                      <div className='flex flex-wrap gap-1'>
                        {instructions.supported.models.map((model) => (
                          <Chip key={model} size='sm' variant='flat'>{model}</Chip>
                        ))}
                      </div>
                      )
                    : null}
                  {instructions.supported.note !== undefined ? <p className='mt-2 text-tiny text-default-600'>{instructions.supported.note}</p> : null}
                </div>
                )
              : null}

            {instructions.unsupported !== undefined
              ? (
                <div className='rounded-lg border-1 border-warning-200 bg-warning-50 p-3'>
                  <p className='mb-1 text-tiny font-bold uppercase text-warning-700'>{instructions.unsupported.label}</p>
                  <p className='text-tiny text-default-700'>{instructions.unsupported.note}</p>
                </div>
                )
              : null}

            <p className='text-tiny text-default-500 italic'>{BATTERY_CRITERIA}</p>
          </ModalBody>
          <ModalFooter>
            {instructions.pdfUrl !== undefined
              ? (
                <a
                  href={instructions.pdfUrl}
                  target='_blank'
                  rel='noreferrer'
                  download='Captura Estado Batería.pdf'
                  className={cn(buttonClasses({ variant: 'ghost' }))}
                >
                  Descargar Instrucciones detalladas (PDF)
                </a>
                )
              : null}
            <Button onClick={() => { setOpenInstructions(false) }}>Entendido</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* Sin footer compartido (página de detalle clásica) el botón del paso se dibuja acá. */}
      {inlineAction && <div className='flex justify-end pt-4'>{inlineAction}</div>}
    </StepCard>
  )
}

export default SwitchBattery
